import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Loader from '@/components/loader'
import { Button } from '@/components/customer-ui/button'
import { Input } from '@/components/customer-ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff, RefreshCw } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import AuthShell from '@modules/manifera-user/resources/js/pages/customer-app/ev-journey/components/AuthShell'
import { useCustomerAccountAuthentication } from '@modules/manifera-user/resources/js/pages/customer-app/use-customer-account-authentication'
import { useQueryClient } from '@tanstack/react-query'
import client from '@/http/customer-client'
import { ApiChargingStation } from '@modules/manifera-user/resources/js/types'
import z from 'zod'
import { reportSchema } from '../../../page-report-overview/schema'
import { cn } from '@/lib/utils'

type CaptchaChallenge = {
  img: string
  key: string
}

type CaptchaStatus = {
  failed_attempts: number
  requires_captcha: boolean
}

type RequiredFieldErrors = {
  captcha: boolean
  email: boolean
  password: boolean
}

export default function CustomerLogin() {
  const navigate = useNavigate()
  const { performLogin, isAuthenticated, isLoggingIn, isValidating } =
    useCustomerAccountAuthentication()
  const [captcha, setCaptcha] = useState('')
  const [captchaChallenge, setCaptchaChallenge] =
    useState<CaptchaChallenge | null>(null)
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false)
  const [isLoadingCaptchaStatus, setIsLoadingCaptchaStatus] = useState(false)
  const [requiresCaptcha, setRequiresCaptcha] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [requiredFieldErrors, setRequiredFieldErrors] =
    useState<RequiredFieldErrors>({
      captcha: false,
      email: false,
      password: false
    })
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isAuthenticated) {
      void navigate('/customer', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const loadCaptchaStatus = async () => {
    setIsLoadingCaptchaStatus(true)

    try {
      const response = await client.get<CaptchaStatus>(
        '/app-api/web-login/captcha-status'
      )
      setRequiresCaptcha(response.data.requires_captcha)

      if (!response.data.requires_captcha) {
        setCaptcha('')
        setCaptchaChallenge(null)
        setRequiredFieldErrors((prev) => ({
          ...prev,
          captcha: false
        }))
      }

      return response.data
    } catch {
      toast({
        title: 'Loginbeveiliging laden mislukt. Probeer het opnieuw.',
        variant: 'destructive'
      })
      return null
    } finally {
      setIsLoadingCaptchaStatus(false)
    }
  }

  const loadCaptcha = async () => {
    setIsLoadingCaptcha(true)

    try {
      const response = await client.get<CaptchaChallenge>(
        '/captcha/api/default'
      )
      setCaptchaChallenge(response.data)
      setCaptcha('')
      setRequiredFieldErrors((prev) => ({
        ...prev,
        captcha: false
      }))
    } catch {
      toast({
        title: 'Captcha laden mislukt. Probeer het opnieuw.',
        variant: 'destructive'
      })
    } finally {
      setIsLoadingCaptcha(false)
    }
  }

  useEffect(() => {
    void loadCaptchaStatus()
  }, [])

  useEffect(() => {
    if (!requiresCaptcha || captchaChallenge != null) {
      return
    }

    void loadCaptcha()
  }, [captchaChallenge, requiresCaptcha])

  const submitLogin = async () => {
    const nextErrors: RequiredFieldErrors = {
      email: email.trim() === '',
      password: password.trim() === '',
      captcha: requiresCaptcha && captcha.trim() === ''
    }

    setRequiredFieldErrors(nextErrors)

    if (nextErrors.email || nextErrors.password || nextErrors.captcha) {
      return
    }

    if (requiresCaptcha && captchaChallenge == null) {
      toast({
        title: 'Captcha is nog niet geladen. Probeer het opnieuw.',
        variant: 'destructive'
      })
      return
    }

    await performLogin({
      email,
      password,
      captcha: requiresCaptcha ? captcha : undefined,
      captchaKey: requiresCaptcha ? captchaChallenge?.key : undefined
    })
      .then(async () => {
        await queryClient.prefetchQuery({
          queryKey: ['customer-stations'],
          queryFn: async () => {
            const res = await client.get<{ data: ApiChargingStation[] }>(
              '/app-api/me/stations'
            )

            return res.data.data
          },
          staleTime: 60 * 1000
        })

        void queryClient.prefetchQuery({
          queryKey: ['customer-reports'],
          queryFn: async () => {
            const response = await client.get('/app-api/me/reports')

            const parsed = z
              .object({
                data: z.array(reportSchema)
              })
              .parse(response.data)

            return parsed.data
          },
          staleTime: 60 * 1000
        })
      })
      .catch(async () => {
        const status = await loadCaptchaStatus()

        if (status?.requires_captcha) {
          await loadCaptcha()
        }
      })
  }

  if (isValidating || isAuthenticated) {
    return <Loader />
  }

  return (
    <AuthShell title='Welkom terug' subtitle='Log in op uw Zeres account'>
      <div
        className='space-y-5'
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            void submitLogin()
          }
        }}
      >
        <div className='space-y-2'>
          <Label
            htmlFor='email'
            className={requiredFieldErrors.email ? 'text-destructive' : ''}
          >
            E-mailadres
          </Label>
          <Input
            id='email'
            type='email'
            placeholder='naam@voorbeeld.nl'
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setRequiredFieldErrors((prev) => ({
                ...prev,
                email: false
              }))
            }}
            className={cn(
              'h-12',
              requiredFieldErrors.email
                ? 'border-destructive focus-visible:ring-destructive'
                : ''
            )}
            style={
              requiredFieldErrors.email
                ? { borderColor: 'var(--destructive)' }
                : undefined
            }
            aria-invalid={requiredFieldErrors.email}
            required
          />
          {requiredFieldErrors.email ? (
            <p className='text-destructive text-sm'>Vul uw e-mailadres in.</p>
          ) : null}
        </div>

        <div className='space-y-2'>
          <Label
            htmlFor='password'
            className={requiredFieldErrors.password ? 'text-destructive' : ''}
          >
            Wachtwoord
          </Label>
          <div className='relative'>
            <Input
              id='password'
              type={showPassword ? 'text' : 'password'}
              placeholder='Uw wachtwoord'
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                setRequiredFieldErrors((prev) => ({
                  ...prev,
                  password: false
                }))
              }}
              className={cn(
                'h-12 pr-12',
                requiredFieldErrors.password
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              )}
              style={
                requiredFieldErrors.password
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.password}
              required
            />
            <button
              type='button'
              onClick={() => {
                setShowPassword(!showPassword)
              }}
              className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
            >
              {showPassword ? (
                <EyeOff className='h-5 w-5' />
              ) : (
                <Eye className='h-5 w-5' />
              )}
            </button>
          </div>
          {requiredFieldErrors.password ? (
            <p className='text-destructive text-sm'>Vul uw wachtwoord in.</p>
          ) : null}
        </div>

        {requiresCaptcha ? (
          <div className='space-y-2'>
            <div className='space-y-1'>
              <div className='flex items-center justify-between gap-3'>
                <Label
                  htmlFor='captcha'
                  className={
                    requiredFieldErrors.captcha ? 'text-destructive' : ''
                  }
                >
                  Captcha
                </Label>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-auto px-0 py-1'
                  disabled={isLoadingCaptcha}
                  onClick={() => {
                    void loadCaptcha()
                  }}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoadingCaptcha ? 'animate-spin' : ''}`}
                  />
                  Vernieuwen
                </Button>
              </div>

              <div className='bg-muted/40 flex min-h-24 items-center justify-center rounded-md border p-3'>
                {captchaChallenge ? (
                  <img
                    src={captchaChallenge.img}
                    alt='Captcha'
                    className='max-h-16 w-full object-contain'
                  />
                ) : (
                  <span className='text-muted-foreground text-sm'>
                    Captcha laden...
                  </span>
                )}
              </div>
            </div>

            <Input
              id='captcha'
              type='text'
              placeholder='Voer de captcha in'
              value={captcha}
              onChange={(event) => {
                setCaptcha(event.target.value)
                setRequiredFieldErrors((prev) => ({
                  ...prev,
                  captcha: false
                }))
              }}
              className={cn(
                'h-12',
                requiredFieldErrors.captcha
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              )}
              style={
                requiredFieldErrors.captcha
                  ? { borderColor: 'var(--destructive)' }
                  : undefined
              }
              aria-invalid={requiredFieldErrors.captcha}
              autoComplete='off'
              required
            />
            {requiredFieldErrors.captcha ? (
              <p className='text-destructive text-sm'>Vul de captcha in.</p>
            ) : null}
          </div>
        ) : null}

        <div className='flex justify-end'>
          <Button variant='link'>
            <Link to='/customer/forgot-password'>Wachtwoord vergeten?</Link>
          </Button>
        </div>

        <Button
          type='button'
          className='h-12 w-full text-base font-semibold'
          disabled={
            isLoggingIn ||
            isLoadingCaptchaStatus ||
            (requiresCaptcha && (isLoadingCaptcha || captchaChallenge == null))
          }
          onClick={() => {
            void submitLogin()
          }}
        >
          {isLoggingIn ? 'Inloggen...' : 'Inloggen'}
        </Button>
      </div>

      <p className='text-muted-foreground mt-8 text-center text-sm'>
        Nog geen account?{' '}
        <Link
          to='/customer/register'
          className='text-primary font-medium hover:underline'
        >
          Aanmelden
        </Link>
      </p>

      <div className='text-muted-foreground mt-6 space-y-1 border-t pt-6 text-center text-sm'>
        <p>
          Helpdesk:{' '}
          <a
            href='tel:0850600641'
            className='hover:text-primary transition-colors'
          >
            085 – 060 0641
          </a>
        </p>
        <p>
          <a
            href='mailto:helpdesk@zeres.nl'
            className='hover:text-primary transition-colors'
          >
            helpdesk@zeres.nl
          </a>
        </p>
        <p>
          <a
            href='https://www.zeres.nl'
            target='_blank'
            rel='noopener noreferrer'
            className='hover:text-primary transition-colors'
          >
            www.zeres.nl
          </a>
        </p>
      </div>
    </AuthShell>
  )
}
